import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Observable, combineLatest, filter, map, merge, take } from 'rxjs';
import { Select } from '@ngxs/store';
import { TranslateModule } from '@ngx-translate/core';

import { DataGridComponent } from '@app/shared/components/smart-data-grid/data-grid/data-grid.component';
import { AppDataState } from '@app/shared/stores/app-data.state';
import { GetKpiSafety } from '@app/shared/actions/app-data.action';
import { NgxsActionName, NgxsFormPath } from '@app/shared/constants';
import { SearchByItem, WebApiResponse, WebApiResponseBase } from '@app/shared/classes/general';
import { KpiSafety } from '@app/shared/classes/kpi-safety';
import { AngularMaterialModule } from '@app/shared/modules/angular-material.module';
import { LoadingSpinnerComponent } from '@app/shared/components/loading-spinner/loading-spinner.component';
import { SmartSearchFormComponent } from '@app/shared/components/smart-search-form/smart-search-form.component';
import { MessageDataComponent } from '@app/shared/components/message-data/message-data.component';
import { SpinnerButtonComponent } from '@app/shared/components/spinner-button/spinner-button.component';
import { PageBasedComponent } from '@app/pages/page.based.component';
import { DataGridColumnDef, GetSafetySearchByList } from './safety.constant';
import { SafetyDataGridCell } from '@app/shared/components/smart-data-grid/data-grid-cell-templates/safety-data-grid-cell.component';
import { SafetyEditModalDialogComponent } from '@app/shared/components/edit-modal-dialog/safety-edit-modal-dialog/safety-edit-modal-dialog.component';
import { CommonPageFilterSetting, CommonSearchCriteria } from '@app/shared/models/common-data.model';
import { SiteConfigHelper } from '@app/shared/utils/site-config.helper';
import { DisableEditButtonDirective } from '@app/shared/directive/disable-edit-button.directive';

@Component({
  selector: 'app-safety',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LoadingSpinnerComponent, DataGridComponent, MessageDataComponent, SafetyEditModalDialogComponent, CommonModule, TranslateModule,
    AngularMaterialModule, SmartSearchFormComponent, SpinnerButtonComponent, SafetyDataGridCell, DisableEditButtonDirective],
  templateUrl: './safety.component.html',
  styleUrl: './safety.component.css'
})
export class SafetyComponent extends PageBasedComponent {
  dataGridColumnDef = DataGridColumnDef;
  formPath = NgxsFormPath.KPI_SAFETY_USAGE_SEARCH_FORM;
  searchForm: FormGroup;
  editForm: FormGroup;
  selectedItems: KpiSafety[] = [];

  dataSource$: Observable<KpiSafety[]>;
  oneItemSelected$: Observable<boolean>;
  noItemsSelected$: Observable<boolean>;
  safetySearchByList$: Observable<SearchByItem[]>;

  @Select(AppDataState.currentSafetySearchBy) currentSafetySearchBy$: Observable<CommonSearchCriteria[]>;
  @Select(AppDataState.currentPageFilterSetting(NgxsActionName.GET_KPI_SAFETY)) currentPageFilterSetting$: Observable<CommonPageFilterSetting>;

  @Select(AppDataState.taskExecuting(NgxsActionName.GET_KPI_SAFETY)) searching$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_KPI_SAFETY)) response$: Observable<WebApiResponse<KpiSafety[]>>;
  @Select(AppDataState.taskError(NgxsActionName.GET_KPI_SAFETY)) errorResponse$: Observable<WebApiResponseBase>;

  constructor() {
    super();
    this.functionName = NgxsActionName.GET_KPI_SAFETY;
  }

  override ngOnInit() {
    super.ngOnInit();
    this.currentSafetySearchBy$.pipe(take(1)).subscribe(searchCriteria => this.defaultSearchCriteriaSub.next(searchCriteria));
    this.errorMessage$ = merge(this.commonDataLoadErrorMessage$, this.errorResponse$.pipe(map(errorResponse => this.getErrorMessages([errorResponse]))));

    this.dataSource$ = this.response$.pipe(filter(Boolean), map(c => c.data));
    this.totalCount$ = this.response$.pipe(filter(Boolean), map(c => c.totalCount));

    var kpis$ = this.kpiAllData$.pipe(filter(Boolean), map(c => c.data), filter(Boolean),
      map(kpi => kpi.filter(item => item.kpiCategory.kpiCategoryId === SiteConfigHelper.GetKpiCategoryIdMapping().Safety).flatMap(item => item)));
    this.safetySearchByList$ = combineLatest([this.currentTerminalDropDowns$, this.currentSubRegions$, kpis$, this.userCountry$]).pipe(map((c) => GetSafetySearchByList(c)));
  }

  override onSearch = () => {
    this.selectedItems = [];
    this.store.dispatch(new GetKpiSafety());
  }

  onSearchWithExistedPageFilter = () => {
    this.selectedItems = [];
    this.store.dispatch(new GetKpiSafety(null));
  }

  onGetData = (info: CommonPageFilterSetting) => {
    info.functionName = NgxsActionName.GET_KPI_SAFETY;
    this.store.dispatch(new GetKpiSafety(info));
  }

  onEdit = () => {
    this.dialog.open(SafetyEditModalDialogComponent, {
      width: '500px',
      data: { data: this.selectedItems[0], successHandler: this.onSearchWithExistedPageFilter },
      disableClose: true
    });
  }

  rowSelected = (data: KpiSafety[]) => {
    this.selectedItems = data ?? [];
  }
}

